package wat.ui

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import wat.Supa
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*
import wat.service.DataService

object MainApp {
    private var session: dynamic = null
    private var userId = ""
    var currentPage = try { window.sessionStorage.getItem("wat_page") ?: "train" } catch (_: Exception) { "train" }
    private var dataLoaded = false
    var showAddForm = false
    var showDiaryForm = false
    var showTrainForm = false
    private lateinit var contentArea: HTMLDivElement
    private lateinit var root: HTMLDivElement

    fun render(sess: dynamic): HTMLElement {
        session = sess
        userId = sess.user.id.toString()
        DebugLog.log("MainApp.render() userId=$userId")

        root = div { style.cssText = "min-height:100vh;background:${Styles.BG};padding-bottom:80px;color:${Styles.TEXT}" }

        // Loading state - hidden initially, shown only if load takes >400ms
        val ldLogoGrad = if (ThemeManager.isDark()) "linear-gradient(135deg,#e2e8f0,#94a3b8)" else "linear-gradient(135deg,#3a3a4a,#5a5a6a)"
        val ldLineGrad = if (ThemeManager.isDark()) "linear-gradient(90deg,#e2e8f0,#64748b)" else "linear-gradient(90deg,#0d9485,#4a52c4)"
        val loadingDiv = div {
            style.cssText = "min-height:100vh;display:flex;align-items:center;justify-content:center;flex-direction:column;opacity:0;transition:opacity 0.3s"
            appendChild(span("TRACTOR") {
                style.cssText = "font-family:'Orbitron',system-ui;font-weight:900;letter-spacing:4px;line-height:1;font-size:24px;background:$ldLogoGrad;-webkit-background-clip:text;-webkit-text-fill-color:transparent"
            })
            appendChild(div {
                style.cssText = "height:1px;width:70px;background:$ldLineGrad;opacity:0.3;border-radius:1px;margin-top:3px"
            })
            appendChild(div {
                style.cssText = "font-size:5.5px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:${Styles.TEXT_40};margin-top:2px"
                textContent = "Health & Activity"
            })
            appendChild(div {
                style.cssText = "font-size:5.5px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:${Styles.TEXT_40};margin-top:1px"
                textContent = "Planner & Tracker"
            })
            appendChild(div {
                id = "db-status"
                style.cssText = "color:${Styles.TEXT_15};font-size:10px;margin-top:16px;letter-spacing:2px;font-weight:500"
            })
        }
        // Inject keyframes once
        if (document.getElementById("wat-loading-css") == null) {
            val styleEl = document.createElement("style")
            styleEl.id = "wat-loading-css"
            styleEl.textContent = "@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{to{opacity:1}}"
            document.head?.appendChild(styleEl)
        }
        root.appendChild(loadingDiv)
        // Show loading only if it takes >400ms
        val loadingTimer = window.setTimeout({ loadingDiv.style.opacity = "1" }, 3000)

        // Load data
        GlobalScope.launch {
            try {
                DebugLog.log("Starting DataService.loadAll()...")
                val ok = DataService.loadAll(userId)
                DebugLog.log("DataService.loadAll() returned: $ok", if (ok) "OK" else "ERROR")
                if (ok) {
                    window.clearTimeout(loadingTimer)
                    dataLoaded = true
                    I18n.currentLang = DataService.profile.lang
                    if (DataService.profile.height in 80..250) {
                        DebugLog.ok("Profile OK: height=${DataService.profile.height}")
                        // Keep current page on refresh, only use defaultPage on fresh login
                        val savedPage = try { window.sessionStorage.getItem("wat_page") } catch (_: Exception) { null }
                        if (savedPage != null && savedPage in listOf("train", "home", "diary", "sett")) {
                            currentPage = savedPage
                        } else {
                            val dp = DataService.profile.defaultPage
                            currentPage = if (dp in listOf("train", "home", "diary")) dp else "train"
                            try { window.sessionStorage.setItem("wat_page", currentPage) } catch (_: Exception) {}
                        }
                        root.clear()
                        buildMainUI()
                    } else {
                        DebugLog.warn("Profile incomplete - showing onboarding")
                        root.clear()
                        root.appendChild(OnboardingScreen.render(userId) {
                            root.clear()
                            GlobalScope.launch {
                                DataService.loadProfile(userId)
                                DataService.loadEntries(userId)
                                buildMainUI()
                            }
                            Unit
                        })
                    }
                } else {
                    DebugLog.error("DataService.loadAll() returned FALSE")
                    document.getElementById("db-status")?.let {
                        it.textContent = t("db_error") + " - tap bug icon"
                        (it as HTMLElement).style.color = "#fb7185"
                    }
                }
            } catch (e: Exception) {
                DebugLog.error("loadAll EXCEPTION: ${e.message}")
                DebugLog.error("Stack: ${e.stackTraceToString()}")
                document.getElementById("db-status")?.let {
                    it.textContent = "Error: ${e.message}"
                    (it as HTMLElement).style.color = "#fb7185"
                }
            }
        }

        return root
    }

    private fun buildMainUI() {
        DebugLog.log("Building main UI...")
        root.appendChild(buildHeader())
        contentArea = div { style.cssText = "padding:0 clamp(12px,3vw,20px)" }
        root.appendChild(contentArea)
        root.appendChild(buildBottomNav())
        root.appendChild(buildFloatingButton())
        renderPage()
        startDbPing()
        DebugLog.ok("Main UI built, page=$currentPage")
    }

    private var pingIntervalId: Int = 0

    private fun startDbPing() {
        // Cancel previous interval if any
        if (pingIntervalId != 0) window.clearInterval(pingIntervalId)

        fun ping() {
            GlobalScope.launch {
                val dot = document.getElementById("db-live-dot") as? HTMLElement ?: return@launch
                try {
                    val res = Supa.select("units", "key")
                    val ok = res.error == null || res.error == undefined
                    dot.style.background = if (ok) "#2dd4bf" else "#fb7185"
                    dot.style.boxShadow = if (ok) "0 0 6px #2dd4bf" else "0 0 6px #fb7185"
                } catch (_: Exception) {
                    dot.style.background = "#fb7185"
                    dot.style.boxShadow = "0 0 6px #fb7185"
                }
            }
        }

        // Ping now + every 30s
        ping()
        pingIntervalId = window.setInterval({ ping() }, 30000)
    }

    private fun buildHeader(): HTMLElement {
        return div {
            style.cssText = "padding:clamp(6px,1.5vw,10px) clamp(12px,3vw,20px) 0;display:flex;align-items:center;justify-content:space-between;margin-bottom:12px"

            // Logo block
            appendChild(div {
                style.cssText = "display:flex;align-items:center;gap:10px"

                // TRACTOR Logo — Orbitron, line under subtitle
                val logoGrad = if (ThemeManager.isDark()) "linear-gradient(135deg,#e2e8f0,#94a3b8)" else "linear-gradient(135deg,#3a3a4a,#5a5a6a)"
                val lineGrad = if (ThemeManager.isDark()) "linear-gradient(90deg,#e2e8f0,#64748b)" else "linear-gradient(90deg,#3a3a4a,#5a5a6a)"
                val logoFont = "font-family:'Orbitron',system-ui;font-weight:900;letter-spacing:3px"
                val subColor = Styles.TEXT_SEC
                appendChild(div {
                    appendChild(span("TRACTOR") {
                        style.cssText = "$logoFont;line-height:1;font-size:16px;display:block;background:$logoGrad;-webkit-background-clip:text;-webkit-text-fill-color:transparent"
                    })
                    appendChild(div {
                        style.cssText = "margin-top:2px"
                        appendChild(div {
                            style.cssText = "height:1px;width:100%;background:$lineGrad;opacity:0.3;border-radius:1px;margin-bottom:2px"
                        })
                        appendChild(div {
                            style.cssText = "font-size:4.5px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:$subColor"
                            textContent = "Health & Activity"
                        })
                        appendChild(div {
                            style.cssText = "font-size:4.5px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:$subColor"
                            textContent = "Planner & Tracker"
                        })
                    })
                })

                // Debug bug icon - small, next to logo
                appendChild(button("\uD83D\uDC1B") {
                    style.cssText = "width:24px;height:24px;border-radius:12px;border:none;background:transparent;color:${Styles.TEXT_08};font-size:10px;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-left:4px"
                    title = "Debug"
                    onclick = {
                        val panel = document.getElementById("debug-panel") as? HTMLElement
                        if (panel != null) {
                            panel.style.display = if (panel.style.display == "none") "flex" else "none"
                        }
                    }
                })
            })

            // Right side: context-dependent
            if (currentPage == "sett") {
                // Flags from flagcdn.com CDN (ISO 3166 alpha-2 codes)
                val langFlagCodes = mapOf("sk" to "sk", "en" to "gb", "cz" to "cz", "de" to "de")

                val hdrBtnBg = if (ThemeManager.isDark()) "rgba(255,255,255,0.1)" else "rgba(0,0,0,0.06)"
                val hdrBtnBorder = if (ThemeManager.isDark()) "rgba(255,255,255,0.18)" else "rgba(0,0,0,0.12)"
                val hdrBtnStyle = "height:36px;border-radius:12px;border:1.5px solid $hdrBtnBorder;background:$hdrBtnBg;cursor:pointer;display:flex;align-items:center;justify-content:center"
                appendChild(div {
                    style.cssText = "display:flex;align-items:center;gap:8px"

                    // Flag button - real flags from CDN
                    appendChild(div {
                        style.cssText = "position:relative"

                        val flagCode = langFlagCodes[I18n.currentLang] ?: "sk"

                        // Flag image button
                        val langBtn = div {
                            style.cssText = "$hdrBtnStyle;padding:0 12px;gap:6px"
                            innerHTML = "<img src='https://flagcdn.com/28x21/${flagCode}.png' width='22' height='16' style='border-radius:2px;display:block' alt=''/><svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='${Styles.TEXT_60}' stroke-width='2.5' stroke-linecap='round'><path d='M6 9l6 6 6-6'/></svg>"
                        }
                        appendChild(langBtn)

                        // Dropdown
                        val dropdown = div {
                            style.cssText = "display:none;position:absolute;right:0;top:36px;background:${Styles.DROPDOWN_BG};border:1.5px solid ${Styles.CARD_BORDER};border-radius:12px;padding:4px;min-width:180px;z-index:200;backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);box-shadow:${Styles.DROPDOWN_SHADOW}"
                        }
                        I18n.langNames.forEach { (code, name) ->
                            val isActive = I18n.currentLang == code
                            val fc = langFlagCodes[code] ?: code
                            dropdown.appendChild(div {
                                style.cssText = "display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:8px;cursor:pointer;background:${if (isActive) "${Styles.TEXT_06}" else "transparent"}"
                                // Flag image
                                innerHTML = "<img src='https://flagcdn.com/28x21/${fc}.png' width='24' height='18' style='border-radius:3px;flex-shrink:0' alt=''/>"
                                // Name
                                appendChild(span(name) {
                                    style.cssText = "flex:1;font-size:14px;font-weight:${if (isActive) "700" else "500"};color:${if (isActive) "${Styles.TEXT}" else "${Styles.TEXT_45}"}"
                                })
                                if (isActive) {
                                    appendChild(span("\u2713") { style.cssText = "font-size:13px;color:${Styles.A1};font-weight:800" })
                                }
                                onclick = {
                                    I18n.currentLang = code
                                    GlobalScope.launch {
                                        val p = DataService.profile.copy(lang = code)
                                        DataService.saveProfile(userId, p)
                                        DataService.profile = p
                                    }
                                    refreshUI()
                                    Unit
                                }
                            })
                        }
                        appendChild(dropdown)

                        langBtn.onclick = {
                            val vis = dropdown.style.display == "block"
                            dropdown.style.display = if (vis) "none" else "block"
                            if (!vis) {
                                val handler: (dynamic) -> Unit = { _ -> dropdown.style.display = "none" }
                                window.setTimeout({
                                    document.addEventListener("click", handler)
                                    window.setTimeout({ document.removeEventListener("click", handler) }, 5000)
                                }, 10)
                            }
                            Unit
                        }
                    })

                    // Theme toggle
                    appendChild(button(if (ThemeManager.isDark()) "\u2600\uFE0F" else "\uD83C\uDF19") {
                        style.cssText = "$hdrBtnStyle;width:36px;font-size:16px;padding:0"
                        title = t("theme_toggle")
                        onclick = {
                            ThemeManager.toggle()
                            refreshUI()
                            Unit
                        }
                    })

                    // DB status dot - live ping
                    appendChild(div {
                        id = "db-live-dot"
                        style.cssText = "width:8px;height:8px;border-radius:4px;background:${Styles.TEXT_QUA};flex-shrink:0"
                    })

                    // Logout button
                    val logoutBtnBg = if (ThemeManager.isDark()) "rgba(251,113,133,0.15)" else "rgba(251,113,133,0.1)"
                    appendChild(button("") {
                        style.cssText = "$hdrBtnStyle;gap:6px;padding:0 14px;border-color:#fb718560;background:$logoutBtnBg;font-family:inherit"
                        innerHTML = "<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#fb7185' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4'/><polyline points='16 17 21 12 16 7'/><line x1='21' y1='12' x2='9' y2='12'/></svg><span style='font-size:13px;font-weight:800;color:#fb7185'>${t("settings_logout").split(" ").first()}</span>"
                        onclick = {
                            GlobalScope.launch { Supa.signOut() }
                            App.init()
                        }
                    })
                })
            }
        }
    }

    // Lucide-style SVG icons for modern bottom nav
    private fun svgIcon(paths: String, size: Int = 20, color: String = "currentColor"): Element {
        val svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        svg.setAttribute("width", "$size")
        svg.setAttribute("height", "$size")
        svg.setAttribute("viewBox", "0 0 24 24")
        svg.setAttribute("fill", "none")
        svg.setAttribute("stroke", color)
        svg.setAttribute("stroke-width", "2")
        svg.setAttribute("stroke-linecap", "round")
        svg.setAttribute("stroke-linejoin", "round")
        svg.innerHTML = paths
        return svg
    }

    // Lucide icon paths
    private val ICON_WEIGHT = "<path d='M12 3v17'/><path d='M5 10h14'/><circle cx='12' cy='7' r='4'/><path d='M4 20h16'/>" // scale
    private val ICON_TRAIN = "<path d='M13 2L3 14h9l-1 8 10-12h-9l1-8z'/>" // zap/lightning
    private val ICON_DIARY = "<path d='M4 19.5A2.5 2.5 0 016.5 17H20'/><path d='M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z'/><line x1='8' y1='7' x2='16' y2='7'/><line x1='8' y1='11' x2='14' y2='11'/>"
    private val ICON_SETTINGS = "<circle cx='12' cy='12' r='3'/><path d='M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z'/>"

    private val isFormOpen get() = showAddForm || showDiaryForm || showTrainForm

    private fun hasBadge(tabKey: String): Boolean {
        val today = wat.model.todayLocal()
        val entries = DataService.entries
        val todayEntry = entries.find { it.date == today }
        return when (tabKey) {
            "home" -> todayEntry == null || todayEntry.weight <= 0
            "train" -> todayEntry == null || todayEntry.sports.isEmpty()
            "diary" -> DataService.diaryEntries.none { it.date == today }
            else -> false
        }
    }

    private fun buildBottomNav(): HTMLElement {
        val inactiveColor = if (ThemeManager.isDark()) "rgba(255,255,255,0.25)" else "rgba(0,0,0,0.35)"
        val navBg = if (ThemeManager.isDark()) "rgba(10,10,16,0.98)" else "rgba(255,255,255,0.96)"
        val navBorder = if (ThemeManager.isDark()) "rgba(255,255,255,0.04)" else "rgba(0,0,0,0.1)"
        val nav = div {
            id = "bottom-nav"
            style.cssText = "position:fixed;bottom:0;left:0;right:0;background:$navBg;backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);border-top:1px solid $navBorder;display:${if (isFormOpen) "none" else "flex"};justify-content:space-around;padding:8px 0 max(8px,env(safe-area-inset-bottom));z-index:100"
        }

        data class NavItem(val key: String, val label: String, val iconPath: String)
        val items = listOf(
            NavItem("train", t("nav_train"), ICON_TRAIN),
            NavItem("home", t("nav_home"), ICON_WEIGHT),
            NavItem("diary", t("nav_diary"), ICON_DIARY),
            NavItem("sett", t("settings_title"), ICON_SETTINGS)
        )

        val activeColor = if (ThemeManager.isDark()) Styles.A1 else "#0a7a6d"
        items.forEach { item ->
            val isActive = currentPage == item.key
            val color = if (isActive) activeColor else inactiveColor
            val badge = hasBadge(item.key)
            nav.appendChild(button("") {
                style.cssText = "flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;background:none;border:none;cursor:pointer;padding:8px 0;position:relative"
                // Active indicator dot
                if (isActive) {
                    appendChild(div {
                        style.cssText = "position:absolute;top:0;left:50%;transform:translateX(-50%);width:20px;height:${if (ThemeManager.isDark()) "2" else "3"}px;border-radius:1.5px;background:$activeColor;${if (ThemeManager.isDark()) "box-shadow:0 0 8px ${Styles.A1}" else ""}"
                    })
                }
                // SVG icon container (for badge positioning)
                appendChild(div {
                    style.cssText = "width:22px;height:22px;display:flex;align-items:center;justify-content:center;color:$color;position:relative"
                    appendChild(svgIcon(item.iconPath, 20, color))
                    // Badge dot
                    if (badge) {
                        appendChild(div {
                            style.cssText = "position:absolute;top:-2px;right:-4px;width:7px;height:7px;border-radius:4px;background:#fb7185;${if (ThemeManager.isDark()) "box-shadow:0 0 6px #fb7185" else ""}"
                        })
                    }
                })
                // Label
                appendChild(span(item.label) {
                    style.cssText = "font-size:${if (isActive) "10" else "9"}px;font-weight:${if (isActive) "800" else "500"};letter-spacing:0.5px;color:$color"
                })
                onclick = {
                    currentPage = item.key
                    try { window.sessionStorage.setItem("wat_page", item.key) } catch (_: Exception) {}
                    showAddForm = false
                    showDiaryForm = false
                    showTrainForm = false
                    TrainTab.resetAddForm()
                    refreshUI()
                }
            })
        }
        return nav
    }

    private fun buildFloatingButton(): HTMLElement {
        val formOpen = isFormOpen
        return button("") {
            id = "fab"
            val fabStyle = if (ThemeManager.isDark()) {
                "border:1.5px solid ${Styles.CARD_BORDER};background:linear-gradient(145deg,${Styles.TEXT_15},${Styles.TEXT_05});backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);box-shadow:0 8px 32px rgba(0,0,0,0.3),inset 0 1px 0 ${Styles.TEXT_20},0 0 0 1px ${Styles.TEXT_05}"
            } else {
                "border:none;background:#3a3a4a;box-shadow:0 3px 16px rgba(0,0,0,0.1)"
            }
            style.cssText = "position:fixed;right:16px;bottom:72px;width:52px;height:52px;border-radius:26px;$fabStyle;cursor:pointer;z-index:99;display:${if (currentPage == "sett" || formOpen) "none" else "flex"};align-items:center;justify-content:center;transition:transform 0.2s;padding:0"
            val strokeCol = if (ThemeManager.isDark()) "rgba(255,255,255,0.9)" else "#fff"
            innerHTML = "<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='$strokeCol' stroke-width='2' stroke-linecap='round'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>"
            onclick = {
                DebugLog.log("FAB clicked, page=$currentPage")
                when (currentPage) {
                    "home" -> { showAddForm = !showAddForm; showDiaryForm = false; showTrainForm = false }
                    "diary" -> { showDiaryForm = !showDiaryForm; showAddForm = false; showTrainForm = false }
                    "train" -> { showTrainForm = !showTrainForm; showAddForm = false; showDiaryForm = false; TrainTab.resetAddForm() }
                    else -> {}
                }
                DebugLog.log("showAddForm=$showAddForm, showDiaryForm=$showDiaryForm, showTrainForm=$showTrainForm")
                updateFabVisibility()
                renderPage()
            }
        }
    }

    private fun updateFabVisibility() {
        val fab = document.getElementById("fab") as? HTMLElement ?: return
        fab.style.display = if (currentPage == "sett" || isFormOpen) "none" else "flex"
    }

    private fun refreshUI() {
        root.clear()
        root.style.cssText = "min-height:100vh;background:${Styles.BG};padding-bottom:80px;color:${Styles.TEXT}"
        buildMainUI()
    }

    fun navigateTo(page: String) {
        currentPage = page
        showAddForm = false
        showDiaryForm = false
        showTrainForm = false
        TrainTab.resetAddForm()
        refreshUI()
    }

    fun renderPage() {
        contentArea.clear()
        updateFabVisibility()
        DebugLog.log("renderPage: page=$currentPage, addForm=$showAddForm, diaryForm=$showDiaryForm, trainForm=$showTrainForm")
        when (currentPage) {
            "home" -> WeightTab.render(contentArea, userId, showAddForm) {
                showAddForm = false
                GlobalScope.launch {
                    DataService.loadEntries(userId)
                    renderPage()
                }
                Unit
            }
            "train" -> TrainTab.render(contentArea, userId, showTrainForm) {
                showTrainForm = false
                GlobalScope.launch {
                    DataService.loadEntries(userId)
                    renderPage()
                }
                Unit
            }
            "diary" -> DiaryTab.render(contentArea, userId, showDiaryForm) {
                showDiaryForm = false
                GlobalScope.launch {
                    DataService.loadDiaryEntries(userId)
                    renderPage()
                }
                Unit
            }
            "sett" -> SettingsTab.render(contentArea, userId) {
                GlobalScope.launch { Supa.signOut() }
                App.init()
            }
        }
    }
}
